import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { updatesApi, type UpdateJob } from '../api';
import { useAsync, useProjectSelection } from '../hooks';
import ProjectPicker from '../components/ProjectPicker';
import { JobStatusBadge } from '../components/StatusBadges';
import { Alert, Button, Empty, Modal, PageHeader, Table, errorText, formatDate } from '../components/ui';

const REFRESH_MS = 15_000;

export default function UpdatesPage() {
  const { projects, list, selected, select } = useProjectSelection();
  const [params, setParams] = useSearchParams();
  const jobs = useAsync(() => (selected ? updatesApi.list(selected.id) : Promise.resolve([])), [selected?.id]);
  const [error, setError] = useState<string | null>(null);

  const openJobId = params.get('job');
  const setOpenJob = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('job', String(id));
    else next.delete('job');
    setParams(next, { replace: true });
  };

  const active = jobs.data?.some((j) => j.status === 'running' || j.status === 'paused');
  const { reload } = jobs;
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(reload, REFRESH_MS);
    return () => clearInterval(timer);
  }, [active, reload]);

  if (projects.error) return <Alert kind="error">{projects.error}</Alert>;
  if (!projects.loading && !selected) return <Empty>No projects available.</Empty>;

  const act = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
      await jobs.reload();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div>
      <PageHeader title="Updates">
        <ProjectPicker projects={list} value={String(selected?.id ?? '')} onChange={select} />
        <Button variant="secondary" onClick={() => jobs.reload()} aria-label="Refresh">
          <RefreshCw size={16} />
        </Button>
      </PageHeader>
      <p className="mb-4 text-sm text-slate-500">Start updates from the Devices page. Devices install their target on their next check-in.</p>
      {error && (
        <div className="mb-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      {jobs.error && <Alert kind="error">{jobs.error}</Alert>}
      {!jobs.loading && jobs.data?.length === 0 ? (
        <Empty>No updates started in this project yet.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>#</th>
              <th>Release</th>
              <th>Scope</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Started</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(jobs.data ?? []).map((job) => (
              <tr key={job.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenJob(job.id)}>
                <td className="text-slate-500">{job.id}</td>
                <td className="font-mono">{job.release_version}</td>
                <td>
                  {job.scope}
                  {job.batch_size && <span className="text-slate-500"> · batches of {job.batch_size}</span>}
                </td>
                <td className="min-w-40">
                  <Progress job={job} />
                </td>
                <td>
                  <JobStatusBadge status={job.status} />
                </td>
                <td className="whitespace-nowrap text-slate-500">
                  {formatDate(job.created_at)}
                  {job.created_by_name && <div className="text-xs">by {job.created_by_name}</div>}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <JobActions job={job} onAction={act} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {openJobId && <JobModal jobId={Number(openJobId)} onClose={() => setOpenJob(null)} onAction={act} />}
    </div>
  );
}

function Progress({ job }: { job: UpdateJob }) {
  const { total, done, failed, cancelled } = job.counts;
  const pct = (n: number) => `${total ? (n / total) * 100 : 0}%`;
  return (
    <div>
      <div className="flex h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="bg-green-500" style={{ width: pct(done) }} />
        <div className="bg-red-500" style={{ width: pct(failed) }} />
        <div className="bg-slate-300" style={{ width: pct(cancelled) }} />
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {done}/{total} done
        {failed > 0 && <span className="text-red-700"> · {failed} failed</span>}
        {job.counts.offered > 0 && ` · ${job.counts.offered} in progress`}
        {job.counts.pending > 0 && ` · ${job.counts.pending} waiting`}
      </div>
    </div>
  );
}

function JobActions({ job, onAction }: { job: UpdateJob; onAction: (action: () => Promise<unknown>) => void }) {
  return (
    <div className="flex justify-end gap-1">
      {job.status === 'running' && (
        <Button variant="secondary" onClick={() => onAction(() => updatesApi.pause(job.id))}>
          Pause
        </Button>
      )}
      {job.status === 'paused' && (
        <Button variant="secondary" onClick={() => onAction(() => updatesApi.resume(job.id))}>
          Resume
        </Button>
      )}
      {(job.status === 'running' || job.status === 'paused') && (
        <Button
          variant="danger"
          onClick={() =>
            confirm('Cancel this update? Devices that have not installed it yet go back to their previous target.') &&
            onAction(() => updatesApi.cancel(job.id))
          }
        >
          Cancel
        </Button>
      )}
    </div>
  );
}

function JobModal({ jobId, onClose, onAction }: { jobId: number; onClose: () => void; onAction: (action: () => Promise<unknown>) => Promise<void> }) {
  const job = useAsync(() => updatesApi.get(jobId), [jobId]);
  const j = job.data;
  const run = async (action: () => Promise<unknown>) => {
    await onAction(action);
    await job.reload();
  };

  return (
    <Modal title={j ? `Update #${j.id} – ${j.release_version}` : 'Update'} onClose={onClose} wide>
      {job.error && <Alert kind="error">{job.error}</Alert>}
      {j && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <JobStatusBadge status={j.status} />
              <span className="text-sm text-slate-500">
                {j.scope}
                {j.batch_size && ` · batches of ${j.batch_size}`} · auto-pause {j.auto_pause ? 'on' : 'off'}
              </span>
            </div>
            <JobActions job={j} onAction={run} />
          </div>
          {j.status === 'paused' && j.auto_pause && (
            <Alert kind="warning">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>Paused. If updated devices reboot too often, Resume keeps it paused. Investigate first, or continue anyway.</span>
                <Button
                  variant="secondary"
                  onClick={() =>
                    confirm('Continue the rollout and ignore reboot warnings from now on?') && run(() => updatesApi.resume(j.id, true))
                  }
                >
                  Continue anyway
                </Button>
              </div>
            </Alert>
          )}
          {j.note && <p className="text-sm whitespace-pre-wrap">{j.note}</p>}
          <Progress job={j} />
          <div className="max-h-96 overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-left text-sm [&_td]:px-3 [&_td]:py-1.5 [&_th]:px-3 [&_th]:py-1.5">
              <thead className="sticky top-0 bg-slate-50 text-slate-500">
                <tr>
                  <th>Device</th>
                  <th>Reports</th>
                  <th>Status</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                {(j.devices ?? []).map((d) => (
                  <tr key={d.device_id} className="border-t border-slate-100">
                    <td>
                      {d.name || d.serial_number}
                      {d.name && <div className="text-xs text-slate-500">{d.serial_number}</div>}
                    </td>
                    <td className="font-mono text-xs">{d.reported_version ?? '—'}</td>
                    <td>
                      <JobStatusBadge status={d.status} />
                    </td>
                    <td className="text-slate-500">{formatDate(d.done_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
